import { Text } from '@s-group/design-system-components/Text/Text'
import { AdditionalText } from '@ui-components/typography/Typography'
import { styled } from 'styled-components'

import { Button, ButtonProps } from '../../actions/Button'
import { BRAND_ICON_DEFAULT_HEIGHT, SokGreenIcon, SokGreenIconNoWhitespace, SpankkiGreenIcon } from '../../assets/Icons'
import { Divider } from '../../decorative/Divider'
import { Colors } from '../../global/colors'
import { FlexRowContainer } from '../../global/CommonStyling'
import { LogoIconType } from '../../global/ComponentProps'
import { BREAKPOINT_DESKTOP_PX, MAIN_HEADER_HEIGHT, MAIN_STEP_HEADER_HEIGHT } from '../../global/constants'
import { SPACE_MEDIUM_PLUS, SPACE_SMALL, SPACE_XXSMALL } from '../../global/spacing'
import { useScreenSizeFlags } from '../../hooks/useScreenSizeFlags'
import { useShouldShowServiceName } from '../../hooks/useShouldShowServiceName'
import { ChangeLanguageButtonOptions, PrimaryTopBarActionButtonOptions, PrimaryTopBarVariantOptions } from './types'
import { getDisplayName, useUserContext } from './UserContext'

export interface PrimaryTopBarProps {
  bottomBorder?: boolean
  actionButtonOptions?: PrimaryTopBarActionButtonOptions
  changeLanguageButtonOptions?: ChangeLanguageButtonOptions
  icon?: LogoIconType
  serviceName?: string
  userDisplayName?: string
  variant?: PrimaryTopBarVariantOptions
  isTransparent?: boolean
}

const DividerMuted = styled(Divider)`
  color: ${Colors.TextWeakNeutral};
  min-height: 14px;
  border-width: 1px;
`

export const PrimaryTopBar = ({
  actionButtonOptions,
  bottomBorder = false,
  changeLanguageButtonOptions,
  icon = LogoIconType.SOK,
  serviceName,
  userDisplayName,
  variant = 'large',
  isTransparent = false,
}: PrimaryTopBarProps) => {
  const { variant: actionButtonVariant = 'plain', ...actionButtonProps } = actionButtonOptions ?? {}

  const actionButtonStyleProps: Omit<ButtonProps, 'label'> = {
    compact: actionButtonVariant === 'plain',
    sizing: variant === 'compact' ? 'small' : 'medium',
    variant: actionButtonVariant,
  }

  const { isDesktopScreenSize, isLargeDesktopScreenSize, isMobileScreenSize } = useScreenSizeFlags()

  const context = useUserContext()
  if (!userDisplayName && context?.user) {
    userDisplayName = getDisplayName(context.user, isDesktopScreenSize, isLargeDesktopScreenSize)
  }
  const secondaryUserName = getDisplayName(context.secondaryUser, true, isLargeDesktopScreenSize)
  const shouldShowServiceName = useShouldShowServiceName(!!secondaryUserName) && variant === 'compact' && serviceName

  const getIcon = () => {
    if (variant === 'compact') {
      return icon === LogoIconType.SPankki && (!isMobileScreenSize || !shouldShowServiceName)
        ? SpankkiGreenIcon(62, BRAND_ICON_DEFAULT_HEIGHT)
        : SokGreenIconNoWhitespace()
    } else {
      return icon === LogoIconType.SPankki ? SpankkiGreenIcon() : SokGreenIcon()
    }
  }

  const divider = <DividerMuted orientation={'vertical'} spacing="xsmall" />

  return (
    <Nav $bottomBorder={variant === 'large' && bottomBorder} $topBarVariant={variant} $isTransparent={isTransparent}>
      {getIcon()}
      <LeftContainer>
        {shouldShowServiceName && (
          <>
            {serviceName && divider}
            <AdditionalText>{serviceName}</AdditionalText>
          </>
        )}
        {secondaryUserName && (
          <>
            {divider}
            <UserDisplayNameText sizing={variant === 'compact' ? 'small' : 'medium'}>
              {secondaryUserName}
            </UserDisplayNameText>
          </>
        )}
      </LeftContainer>
      <RightContainer>
        {variant === 'large' && changeLanguageButtonOptions && (
          <Button
            aria-label={changeLanguageButtonOptions.ariaLabel}
            compact={true}
            data-testid={'primary-top-bar-change-language'}
            label={changeLanguageButtonOptions.label}
            onClick={changeLanguageButtonOptions.onClick}
            variant="plain"
          />
        )}
        <TextAndActionContainer>
          {userDisplayName && (
            <UserDisplayNameText sizing={variant === 'compact' ? 'small' : 'medium'}>
              {userDisplayName}
            </UserDisplayNameText>
          )}
          {actionButtonOptions && actionButtonProps && (
            <ActionButton
              label={actionButtonOptions.label}
              $topBarVariant={variant}
              data-testid={'primary-top-bar-action'}
              {...actionButtonProps}
              {...actionButtonStyleProps}
            />
          )}
        </TextAndActionContainer>
      </RightContainer>
    </Nav>
  )
}

const Nav = styled.nav<{
  $bottomBorder?: boolean
  $topBarVariant: PrimaryTopBarVariantOptions
  $isTransparent?: boolean
}>`
  background-color: ${(props) => (props.$isTransparent ? undefined : Colors.BackgroundDefaultNeutral)};
  color: ${Colors.TextStrongNeutral};
  display: flex;
  height: ${(props) =>
    props.$topBarVariant === 'compact' ? `${MAIN_STEP_HEADER_HEIGHT}px` : `${MAIN_HEADER_HEIGHT}px`};
  align-items: center;
  justify-content: space-between;
  padding: 0 ${SPACE_SMALL};
  border-bottom: ${(props) => (props.$bottomBorder ? `1px solid ${Colors.BorderWeakNeutral}` : '0')};
  position: relative;

  @media (max-width: ${BREAKPOINT_DESKTOP_PX - 1}px) {
    background-color: ${Colors.BackgroundDefaultNeutral};
  }
`

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE_MEDIUM_PLUS};
`

const ActionButton = styled(Button)<{ $topBarVariant: PrimaryTopBarVariantOptions }>`
  white-space: nowrap;
`

const LeftContainer = styled(FlexRowContainer)`
  flex: 1;
  align-items: center;
  color: ${Colors.TextWeakNeutral};
  padding-left: ${SPACE_XXSMALL};
`

const TextAndActionContainer = styled.div`
  display: flex;
  align-items: center;
`

const UserDisplayNameText = styled(Text)`
  color: ${Colors.TextStrongNeutral};
  white-space: nowrap;
`
